import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve visitor ID (persists across sessions)
const getVisitorId = () => {
  let visitorId = localStorage.getItem('bidzuu_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('bidzuu_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate session ID (new for each browser session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('bidzuu_session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('bidzuu_session_id', sessionId);
  }
  return sessionId;
};

// Get user's country/city using IP geolocation API (free)
// Note: This may fail due to CORS in some environments - that's expected
const getGeoLocation = async () => {
  try {
    const cached = sessionStorage.getItem('bidzuu_geo');
    if (cached) return JSON.parse(cached);
    
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const response = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
      mode: 'cors'
    });
    clearTimeout(timeout);
    
    if (response.ok) {
      const data = await response.json();
      const geo = {
        country: data.country_name || 'Unknown',
        city: data.city || 'Unknown',
        region: data.region || 'Unknown',
        country_code: data.country_code || 'XX'
      };
      sessionStorage.setItem('bidzuu_geo', JSON.stringify(geo));
      return geo;
    }
  } catch {
    // Silently ignore - geolocation is optional for analytics
  }
  return { country: 'Unknown', city: 'Unknown', country_code: 'XX' };
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const lastPath = useRef(null);
  
  useEffect(() => {
    // Don't track the same page twice in a row
    if (location.pathname === lastPath.current) return;
    lastPath.current = location.pathname;
    
    const trackPageView = async () => {
      try {
        const visitorId = getVisitorId();
        const sessionId = getSessionId();
        const geo = await getGeoLocation();
        const userId = localStorage.getItem('token') ? JSON.parse(atob(localStorage.getItem('token').split('.')[1])).user_id : null;
        
        const payload = {
          event_type: 'page_view',
          visitor_id: visitorId,
          session_id: sessionId,
          user_id: userId,
          page: location.pathname,
          page_title: document.title,
          country: geo.country,
          city: geo.city,
          screen_width: window.screen.width,
          screen_height: window.screen.height
        };
        
        // Send analytics data
        await fetch(`${API_URL}/api/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'include'
        });
      } catch (error) {
        // Silent fail - don't disrupt user experience
        console.log('Analytics tracking error:', error);
      }
    };
    
    // Track with slight delay to ensure page is fully loaded
    const timer = setTimeout(trackPageView, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);
  
  // Heartbeat to track active users (every 2 minutes)
  useEffect(() => {
    const heartbeat = async () => {
      try {
        const visitorId = getVisitorId();
        const sessionId = getSessionId();
        const geo = await getGeoLocation();
        
        await fetch(`${API_URL}/api/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_type: 'heartbeat',
            visitor_id: visitorId,
            session_id: sessionId,
            page: location.pathname,
            country: geo.country,
            city: geo.city
          }),
          credentials: 'include'
        });
      } catch (e) {
        // Silent fail
      }
    };
    
    // Send heartbeat every 2 minutes
    const interval = setInterval(heartbeat, 120000);
    return () => clearInterval(interval);
  }, [location.pathname]);
  
  return null; // This component doesn't render anything
};

export default AnalyticsTracker;
